import React, { useCallback, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRenderCellParams } from '@mui/x-data-grid';

import { useGetRewardsCatalogs } from '~/hooks/apis/rewards/useRewardsCatalogs';
import { useSearchRewardsReservations } from '~/hooks/apis/rewards/useRewardsReservations';

import type {
  RewardsReservation,
  RewardsReservationStatus,
  SearchRewardsReservationsParams,
} from '~/services/LuxLoyalty';
import { formatDateLongMonthWithMeridiem } from '~/services/TimeService';

/** Status options for filtering. */
const STATUS_OPTIONS: Array<{ value: RewardsReservationStatus; label: string }> = [
  { value: 'RESERVED', label: 'Reserved' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'RELEASED', label: 'Released' },
  { value: 'EXPIRED', label: 'Expired' },
];

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Color mapping for the status chip — keeps the grid scannable. */
const STATUS_CHIP_COLOR: Record<RewardsReservationStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  RESERVED: 'primary',
  CONFIRMED: 'success',
  RELEASED: 'default',
  EXPIRED: 'warning',
};

/** Formats an ISO timestamp for the grid; falls back to the raw value. */
function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateLongMonthWithMeridiem(value) || value;
}

/**
 * Admin dashboard listing all classic-reward reservations with filters and
 * pagination. Filters are AND-combined server-side; URL query params
 * (e.g. `?catalogEntryId=...&catalogId=...`) pre-apply on first load so deep
 * links from other pages (notably the inventory dialog's "View reservations"
 * button) land on a pre-filtered grid.
 */
export default function ClassicRewardsReservationsPage(): React.JSX.Element {
  const { search } = useLocation();

  // ---- URL pre-fill (deep-link entry) ----
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const prefilledOrderId = urlParams.get('orderId') ?? '';
  const prefilledInventoryId = urlParams.get('inventoryId') ?? '';
  const prefilledCatalogEntryId = urlParams.get('catalogEntryId') ?? '';
  const prefilledCatalogId = urlParams.get('catalogId') ?? '';
  const prefilledStatusRaw = urlParams.get('status') ?? '';
  const prefilledStatus: RewardsReservationStatus | '' = STATUS_OPTIONS.some((o) => o.value === prefilledStatusRaw)
    ? (prefilledStatusRaw as RewardsReservationStatus)
    : '';

  // ---- Filter input state (uncommitted) ----
  const [orderIdInput, setOrderIdInput] = useState(prefilledOrderId);
  const [inventoryIdInput, setInventoryIdInput] = useState(prefilledInventoryId);
  const [catalogEntryIdInput, setCatalogEntryIdInput] = useState(prefilledCatalogEntryId);
  const [catalogIdInput, setCatalogIdInput] = useState(prefilledCatalogId);
  const [statusInput, setStatusInput] = useState<RewardsReservationStatus | ''>(prefilledStatus);

  // ---- Applied filters (committed; drive the query) ----
  // Initialised from URL params so deep links auto-run the search.
  const [appliedFilters, setAppliedFilters] = useState<{
    orderId?: string;
    inventoryId?: string;
    catalogEntryId?: string;
    catalogId?: string;
    status?: RewardsReservationStatus;
  }>(() => ({
    ...(prefilledOrderId ? { orderId: prefilledOrderId } : {}),
    ...(prefilledInventoryId ? { inventoryId: prefilledInventoryId } : {}),
    ...(prefilledCatalogEntryId ? { catalogEntryId: prefilledCatalogEntryId } : {}),
    ...(prefilledCatalogId ? { catalogId: prefilledCatalogId } : {}),
    ...(prefilledStatus ? { status: prefilledStatus } : {}),
  }));

  // ---- Pagination state ----
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // ---- Catalog list (for the catalog filter dropdown) ----
  const { data: catalogs } = useGetRewardsCatalogs();

  // ---- Reservations query ----
  const queryParams: SearchRewardsReservationsParams = useMemo(
    () => ({
      ...appliedFilters,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      sort: 'createdAt',
      direction: 'desc',
    }),
    [appliedFilters, paginationModel],
  );

  const { data, isFetching, error } = useSearchRewardsReservations(queryParams);

  const rows = data?.reservations ?? [];
  const totalCount = data?.total ?? 0;

  // ---- Handlers ----

  const handleSearch = useCallback(() => {
    setAppliedFilters({
      ...(orderIdInput.trim() ? { orderId: orderIdInput.trim() } : {}),
      ...(inventoryIdInput.trim() ? { inventoryId: inventoryIdInput.trim() } : {}),
      ...(catalogEntryIdInput.trim() ? { catalogEntryId: catalogEntryIdInput.trim() } : {}),
      ...(catalogIdInput.trim() ? { catalogId: catalogIdInput.trim() } : {}),
      ...(statusInput ? { status: statusInput } : {}),
    });
    // Reset back to page 0 — new filters shouldn't open mid-pagination.
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [orderIdInput, inventoryIdInput, catalogEntryIdInput, catalogIdInput, statusInput]);

  const handleClearFilters = useCallback(() => {
    setOrderIdInput('');
    setInventoryIdInput('');
    setCatalogEntryIdInput('');
    setCatalogIdInput('');
    setStatusInput('');
    setAppliedFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // ---- Grid columns ----
  const columns = useMemo<Array<GridColDef<RewardsReservation>>>(
    () => [
      {
        field: 'orderId',
        headerName: 'Order',
        width: 280,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, string>) => (
          <Link
            component={RouterLink}
            to={`/purchases/${params.value}`}
            target="_blank"
            rel="noopener"
            underline="hover"
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, RewardsReservationStatus>) => (
          <Chip size="small" label={params.value} color={params.value ? STATUS_CHIP_COLOR[params.value] : 'default'} />
        ),
      },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 70,
        type: 'number',
        sortable: false,
      },
      {
        field: 'releasedQuantity',
        headerName: 'Released',
        width: 90,
        type: 'number',
        sortable: false,
      },
      {
        field: 'pointsCharged',
        headerName: 'Points',
        width: 100,
        type: 'number',
        sortable: false,
      },
      {
        field: 'cashCharged',
        headerName: 'Cash',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, number>) =>
          `${(params.value ?? 0).toFixed(2)} ${params.row.cashCurrency}`,
      },
      {
        field: 'catalogEntryId',
        headerName: 'Catalog Entry',
        width: 280,
        sortable: false,
      },
      {
        field: 'reservedAt',
        headerName: 'Reserved',
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, string>) => formatTimestamp(params.value),
      },
      {
        field: 'confirmedAt',
        headerName: 'Confirmed',
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, string | null>) => formatTimestamp(params.value),
      },
      {
        field: 'releasedAt',
        headerName: 'Released At',
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, string | null>) => formatTimestamp(params.value),
      },
      {
        field: 'expiresAt',
        headerName: 'Expires',
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RewardsReservation, string>) => formatTimestamp(params.value),
      },
    ],
    [],
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h5" fontWeight={600}>
          Charter Flight Rewards — Reservations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search classic-reward reservations across all orders. Filters combine with AND.
        </Typography>
      </Stack>

      {/* --- Filters --- */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order ID"
                  value={orderIdInput}
                  onChange={(e) => setOrderIdInput(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Catalog entry ID"
                  value={catalogEntryIdInput}
                  onChange={(e) => setCatalogEntryIdInput(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Inventory ID"
                  value={inventoryIdInput}
                  onChange={(e) => setInventoryIdInput(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Catalog"
                  value={catalogIdInput}
                  onChange={(e) => setCatalogIdInput(e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {(catalogs ?? []).map((catalog) => (
                    <MenuItem key={catalog.id} value={catalog.id}>
                      {catalog.itemType} — {catalog.id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusInput}
                  onChange={(e) => setStatusInput(e.target.value as RewardsReservationStatus | '')}
                >
                  <MenuItem value="">Any</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleSearch} disabled={isFetching}>
                Search
              </Button>
              <Button variant="text" onClick={handleClearFilters} disabled={isFetching}>
                Clear filters
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load reservations.'}</Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          getRowId={(row) => row.reservationId}
          columns={columns}
          loading={isFetching}
          autoHeight
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
        />
      </Box>
    </Stack>
  );
}
